/**
 * 장지(자연장·봉안당·묘지) 비용 게이지에 쓸 가격 범위 계산.
 *
 * 입력은 facilities_detail.json의 hallrents(원 단위), 출력은 라벨과 같은 만원 단위다.
 *
 * ── 왜 개별 시설 가격을 안 쓰는가
 *
 * 게이지의 역할은 정확한 시설 가격 고지가 아니라, 스크롤 내내 "여기서 비용을 알 수 있다"는
 * 기대를 유지하는 것이다. 실험 #296에서 게이지를 sticky로 고정했을 때 견적 제출자 중 임박
 * 비율이 24%→33%로 올라갔다. 게이지가 한 일은 가격 전달이 아니라 비용 민감 사용자 선별이었다.
 *
 * 반면 개별 가격을 쓰면 (1) 커버리지가 28%에서 막히고(데이터 없음·단일가·단위 혼재),
 * (2) 낡은 가격을 새 화면에 하나 더 노출한다. facilities_detail.json은 2016~2020년 e하늘
 * 스크랩이고 자하연 사례에서 표시 1,050만원 vs 실제 1,950만원으로 CS가 났다
 * (wiki/features/facilities-price-jahayeon.md).
 *
 * ── 왜 공설/사설로 나누는가
 *
 * 장지 비용의 최대 변수가 공설이냐 사설이냐다. 한 통에 넣으면 어느 쪽도 대표하지 못한다.
 * 시도x유형 집계 21개 중 15개(71%)가 공설/사설로 2배 이상 갈렸다(인천 봉안당은 43배).
 *
 * ── 왜 요약하지 않고 전체 min~max를 쓰는가
 *
 * 중위값 같은 요약을 쓰면 개별 시설이 그 범위를 벗어난다. 실측 14%가 어긋났고, 원인의 절반이
 * "공설인데 고가 상품도 파는" 시설이라(자연숲추모공원 수목장림: 공설, 90~900만원) 분류를
 * 정교하게 해도 풀리지 않았다. 전체 min~max는 정의상 개별 시설을 항상 담는다(471곳 전수
 * 확인, 벗어남 0건). 설명할 통계도 없어서 근거를 물었을 때 답이 꼬이지 않는다.
 */

import { excludeFacilities } from "@/constants/facility/excludeFacilities";

export type FacilityType = "NaturalBurial" | "Charnel" | "Cemetery";

export interface FacilityHallrent {
  item: string;
  detail: string;
  charge: number | null;
}

/** facilities_detail.json 행에서 비용 계산에 필요한 부분만 */
export interface FacilityCostSource {
  id: number | string;
  type?: string;
  companyname?: string;
  is_public?: boolean;
  hallrents?: FacilityHallrent[] | null;
}

export interface FacilityCostRange {
  /** 만원 단위 */
  min: number;
  /** 만원 단위 */
  max: number;
}

/**
 * 유형별 "이론상 납득가능한 최소 금액" (만원).
 *
 * 기준은 **공설 시설의 지역민(관내/도내) 최저 사용료**다. 일반 고객이 실제로 낼 수 있는
 * 가장 싼 값이라 이론적 하한으로 방어된다. 아래를 제외하고 뽑았다.
 * - 관리비·환불규정·재사용료·기간연장·무연유골·유택동산(산골)·추가비용 행: 초기 사용료가 아님
 * - 국가유공자·기초생활수급자·장기기증자·문중 감면가: 특정 자격자만 되는 값이라 일반 하한이 아님
 * - "3.3m2당 1만원" 같은 면적 단가 표기: 총액이 아님
 *
 * 근거 시설:
 * - NaturalBurial 6만원 - 연평리·장봉리·선재리·이작리 공설묘지 자연장지 (관내 주민 사용료)
 * - Charnel 2만원 - 성산읍공설봉안당(관내 2만/관외 5만), 표선면봉안당 개인단(도내 2만/도외 5만)
 * - Cemetery 1만원 - 강화군 공설묘지 다수 (강화군민 15년 사용료)
 *
 * 하한을 뽑을 때 is_public 플래그는 쓰지 않았다. 완도군추모공원봉안당처럼 실질 공설인데
 * false로 들어간 행이 있어 플래그로만 걸면 실제 최저가를 놓친다. 지역민 요금 표현도
 * "관내"만 보면 제주권의 "도내"를 놓친다.
 *
 * 원천 데이터 갱신 시 이 숫자도 같이 검토해야 한다.
 */
export const MIN_ATTAINABLE_MANWON: Record<FacilityType, number> = {
  NaturalBurial: 6,
  Charnel: 2,
  Cemetery: 1,
};

/**
 * 시설 내부 가격 목록에서 잘라낼 상하위 비율.
 *
 * hallrents는 수용 인원이 다른 상품을 한 배열에 섞는다. charge가 전부 "기당"으로 적혀 있지만
 * 1기가 봉안담 1위일 수도, 가족묘 36위일 수도 있다(시안 가족추모공원 id 174: 2,085만원 ~
 * 3억 2,800만원). 그대로 min~max를 잡으면 단위 차이가 가격 차이로 표시된다.
 *
 * 이전에는 배율이 10배를 넘는 시설을 집계에서 통째로 빼는 방식이었는데, 상품 종류가 많은
 * **사설이 집중적으로 탈락**해서 모수가 공설로 쏠렸다(묘지 모수 88곳 중 공설 73%). 그 결과
 * 전국 묘지 범위가 30~96만원으로 내려앉아, 실제 150~3,700만원인 자하연 일산(id 657) 같은
 * 사설 페이지와 20~40배 어긋났다.
 *
 * 시설을 버리지 않고 시설 안에서 상하위만 자르면 사설이 모수에 정상 참여한다
 * (사설 표본 봉안당 89→174, 묘지 24→57, 자연장 13→34).
 *
 * 주의: 이 절단은 **시설 안**에서만 한다. 시설 간 집계는 절단하지 않는다. 그래야 개별 시설이
 * 버킷 범위 안에 담기는 성질이 유지된다.
 *
 * 원천 데이터에 단위(위수) 필드가 생기면 절단 대신 단위별로 범위를 잡는 게 맞다.
 */
const TRIM_RATIO = 0.1;

/** 공설 판정용 이름 신호. is_public이 false인 653곳 중 7곳이 여기 걸린다 */
const PUBLIC_NAME_PATTERN = /공설|시립|군립|도립|국립|시영|군영/;

/** 집계 버킷당 요구 최소 표본. 미달이면 게이지를 접는다 */
const MIN_SAMPLE = 10;

/**
 * 게이지 라벨에 쓰는 유형 표기.
 *
 * `facilityTypeLabel.ts` 의 label 과 Charnel 에서 갈린다(여기는 "봉안당", 그쪽은 "납골당").
 * 그쪽은 SEO title·description 용이라 검색량이 많은 표기를 쓰고, 여기는 비용 화면이라
 * 정확한 표기를 쓴다. 로그인 화면 recap 의 유형 칩은 같은 카드 안 게이지와 말이 맞아야
 * 하므로 이 맵을 쓴다.
 */
export const COST_GAUGE_TYPE_LABEL: Record<FacilityType, string> = {
  NaturalBurial: "자연장",
  Charnel: "봉안당",
  Cemetery: "묘지",
};

/**
 * 공설 여부.
 *
 * is_public 플래그만으로는 완도군추모공원봉안당·함양군구룡공설공원묘지처럼 실질 공설인데
 * false인 행을 놓친다. 이름 신호를 OR로 묶어 보완한다. 개별 시설에 시세를 붙이는 판정이
 * 아니라 집계 버킷을 나누는 용도라 이 정도 정확도로 충분하다.
 */
export const isPublicFacility = (facility: FacilityCostSource): boolean =>
  Boolean(facility.is_public) || PUBLIC_NAME_PATTERN.test(facility.companyname ?? "");

/**
 * 시설 하나의 대표 가격 범위를 만원 단위로 돌려준다.
 *
 * 제외 행:
 * - 관리비 (시설 이용가가 아니라 연 단위 유지비)
 * - 만원 미만 (보훈 감면 0원, 환불 규정 행, 금액 자리의 1원 플레이스홀더, 6천원 부페 등)
 *
 * 남은 가격에서 상하위 TRIM_RATIO를 자르고, 최저가가 유형 하한보다 낮으면 하한으로 올린다.
 *
 * @param facility - facilities_detail.json 행
 * @returns 만원 단위 범위. 쓸 가격이 없거나 절단 후 최저 >= 최고면 null
 */
export function getFacilityCostRange(
  facility: FacilityCostSource | undefined | null,
): FacilityCostRange | null {
  if (!facility) return null;

  const { hallrents, type } = facility;
  if (!Array.isArray(hallrents)) return null;

  const charges = hallrents
    .filter(
      (hallrent) => typeof hallrent?.charge === "number" && !hallrent.item?.includes("관리비"),
    )
    // 만원 단위로 내림한 뒤 0을 걸러 게이지에 "0만원"이 찍히지 않게 한다.
    .map((hallrent) => Math.floor((hallrent.charge as number) / 10000))
    .filter((manwon) => manwon > 0)
    .sort((a, b) => a - b);

  if (charges.length === 0) return null;

  // 양 끝에서 각각 이만큼 버린다. 개수가 적으면 0이 되어 전 구간을 쓴다.
  const trim = Math.floor(charges.length * TRIM_RATIO);
  const low = charges[trim];
  const high = charges[charges.length - 1 - trim];

  const min = Math.max(low, MIN_ATTAINABLE_MANWON[type as FacilityType] ?? 0);
  if (high <= min) return null;

  return { min, max: high };
}

export interface FacilityCostGauge extends FacilityCostRange {
  /**
   * 게이지 제목. 표시 숫자의 정체를 그대로 적는다.
   *
   * "평균"·"중위값" 같은 통계 요약어를 쓰지 않는다. 요약을 하는 순간 개별 시설이 그 범위를
   * 벗어나고(중위값 방식에서 14%가 어긋났다), 근거를 물었을 때 설명이 꼬인다.
   * 전체 min~max는 요약이 아니라 실제 범위라, 개별 시설이 항상 그 안에 담긴다.
   */
  label: string;
  /** 막대 아래 근거 문구. 표본 수를 밝힌다 */
  note: string;
}

const KIND_LABEL = { public: "공설", private: "사설" } as const;

type BucketKey = `${string}|${keyof typeof KIND_LABEL}`;

// getStaticProps가 시설당 한 번씩(1,235회) 호출되므로 집계를 프로세스당 한 번만 만든다.
// 입력이 항상 같은 facilities_detail.json이라 첫 호출 기준 캐시로 충분하다.
let bucketCache: Map<BucketKey, FacilityCostRange[]> | null = null;

const bucketKey = (type: string, isPublic: boolean): BucketKey =>
  `${type}|${isPublic ? "public" : "private"}`;

function buildBuckets(all: readonly FacilityCostSource[]): Map<BucketKey, FacilityCostRange[]> {
  if (bucketCache) return bucketCache;

  const buckets = new Map<BucketKey, FacilityCostRange[]>();

  for (const facility of all) {
    if (!facility.type) continue;
    // 가격 비공개 요청 업체의 가격은 집계에도 넣지 않는다. 1,235곳 중 11곳이라 통계 손실은
    // 없고, 계약 취지를 두고 다툴 여지를 남기지 않는다.
    if (excludeFacilities.includes(String(facility.id))) continue;

    const range = getFacilityCostRange(facility);
    if (!range) continue;

    const key = bucketKey(facility.type, isPublicFacility(facility));
    const bucket = buckets.get(key);
    if (bucket) bucket.push(range);
    else buckets.set(key, [range]);
  }

  bucketCache = buckets;
  return buckets;
}

/**
 * 비용 게이지에 쓸 범위와 문구를 정한다.
 *
 * 해당 시설이 속한 쪽(공설이면 공설, 사설이면 사설)의 **전체 min~max**를 돌려준다.
 * 요약하지 않는 이유는 두 가지다.
 *
 * 1) 개별 시설이 항상 담긴다. 정의상 각 시설 범위는 자기 버킷의 min~max 안에 있다.
 *    471곳 전수 확인 결과 벗어남 0건. 중위값으로 요약했을 때는 14%가 어긋났는데, 그 원인의
 *    절반이 "공설인데 고가 상품도 파는" 시설이라 분류를 정교하게 해도 풀리지 않았다.
 *    (예: 자연숲추모공원 수목장림은 공설이면서 90~900만원)
 *
 * 2) 설명할 통계가 없다. "공설 봉안당 비용 범위 2~975만원"은 그냥 사실이다.
 *
 * 대가는 범위가 넓다는 것이다(공설 2~975만원 / 사설 3~6,400만원으로 두 범위가 상당히 겹친다).
 * 좁히려면 양 끝을 절단해야 하는데, 그러면 다시 일부 시설이 범위를 벗어난다. 게이지의 역할이
 * 정확한 가격 고지가 아니라 비용 인지 유지(실험 #296)라서, 넓지만 참인 범위를 택했다.
 *
 * 가격 비공개 요청 시설은 게이지 자체를 띄우지 않는다. 전국 통계는 그 업체 가격이 아니라
 * 계약 위반은 아니지만, 그 시설 페이지에 비용 게이지가 보이는 것 자체가 취지에 어긋난다.
 *
 * facilities_detail.json 전체를 받으므로 **getStaticProps에서만 호출**할 것.
 * 컨테이너에서 호출하면 1,235행 JSON이 클라이언트 번들에 실린다.
 *
 * @param facility - 대상 시설 (id로 비공개 판정, type + is_public으로 버킷 결정)
 * @param all - facilities_detail.json 전체
 */
export function resolveFacilityCostGauge(
  facility: FacilityCostSource | undefined | null,
  all: readonly FacilityCostSource[],
): FacilityCostGauge | null {
  if (!facility) return null;
  if (excludeFacilities.includes(String(facility.id))) return null;

  const type = facility.type as FacilityType | undefined;
  if (!type || !COST_GAUGE_TYPE_LABEL[type]) return null;

  const isPublic = isPublicFacility(facility);
  const ranges = buildBuckets(all).get(bucketKey(type, isPublic));
  if (!ranges || ranges.length < MIN_SAMPLE) return null;

  const kindLabel = KIND_LABEL[isPublic ? "public" : "private"];
  const typeLabel = COST_GAUGE_TYPE_LABEL[type];

  return {
    min: Math.min(...ranges.map((r) => r.min)),
    max: Math.max(...ranges.map((r) => r.max)),
    label: `${kindLabel} ${typeLabel} 비용 범위`,
    note: `전국 ${kindLabel} ${typeLabel} ${ranges.length}곳 기준. 시설과 상품에 따라 달라집니다.`,
  };
}
